import React from "react";
import styled from "styled-components";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Article } from "@domain/lms/article/article";
import { ImageVersions } from "@domain/media/dtos";
import { ArticleCategoryAndDate } from "@ui/components/article/articleCategoryAndDate";
import { ArticleDetails } from "@ui/components/article/articleDetails";
import { ArticleThumbnailMenu } from "@ui/components/article/articleThumbnailMenu";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { row, Spacer } from "@ui/components/layout";

import defaultThumbnailInsight from "@assets/images/default-thumbnail-insight.png";
import { ArticleStatusLabel } from "@ui/components/article/articleStatusLabel";
import { theme } from "@ui/styles/theme";
import { alpha, gap, mobile } from "@ui/styles/utils";
interface DetailedArticleHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
	article: Article;
}
export const DetailedArticleHeader: React.FC<DetailedArticleHeaderProps> = ({ article, ...htmlProps }) => {
	const { banner, category } = article;
	const currentProfile = useCurrentProfile();
	const isAuthor = article.author?.id === currentProfile?.id;
	return (
		<Container {...htmlProps}>
			<TopSide>
				<StyledBackgroundImage
					image={banner}
					defaultUrl={!banner ? defaultThumbnailInsight : undefined}
					size={ImageVersions.LARGE}
					insideRadius
					forPage
				/>
				<BannerContent>
					<TopRight>
						<StyledMenu article={article} />
					</TopRight>
					<Spacer />
					<Bottom>{isAuthor && <ArticleStatusLabel status={article.status} />}</Bottom>
				</BannerContent>
			</TopSide>
			<ArticleInfos>
				<ArticleDetails color={theme.colors.black} article={article} />
				<ArticleTitle> {article.title}</ArticleTitle>
				{/*<ArticleDescription content={description} />*/}
				<ArticleCategoryAndDate article={article} />
			</ArticleInfos>
			{category && category !== "OTHERS" && <Category category={category} />}
		</Container>
	);
};

const Container = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	position: relative;
	margin-bottom: 0 !important;
`;

const TopSide = styled.div`
	position: relative;
	height: 460px;
	width: 95%;
	overflow: hidden;
	@media (max-width: 900px) {
		height: 200px;
	}
`;

const StyledBackgroundImage = styled(BackgroundImage)`
	width: 100%;
	height: 100%;
`;

const BannerContent = styled.div`
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	border-radius: 25px;
	background-image: linear-gradient(to top, rgba(0, 0, 0, 0.6) 25%, rgba(0, 0, 0, 0.4));
	padding: 30px;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	${gap(20)};
	@media ${mobile} {
		padding: 16px;
		${gap(5)};
	}

	> * {
		color: ${({ theme }) => theme.colors.white};
	}
`;

const ArticleInfos = styled.div`
	padding: 10px 30px 40px;
	@media ${mobile} {
		padding: 30px 16px;
	}
	${gap(15)};
	width: 100%;
	color: black;
`;

const Title = styled.div`
	display: none;
	${({ theme }) => theme.textStyles.title}
`;

const ArticleTitle = styled(Title)`
	display: block;
	font-family: Sora;
	font-style: normal;
	font-weight: 600;
	font-size: 35px;
	min-height: 41px;
	word-break: break-word;
	line-height: 35px;
	@media ${mobile} {
		margin-top: 10px;
		display: block;
		align-self: flex-start;
	}
`;

const Category = styled(CategoryLabel)`
	position: absolute;
	left: 50px;
	top: 30px;

	@media ${mobile} {
		left: 16px;
		top: 16px;
	}
`;

const TopRight = styled.div`
	position: absolute;
	top: 30px;
	right: 30px;

	@media ${mobile} {
		right: 16px;
		top: 16px;
	}
`;
const Bottom = styled.div`
	align-self: stretch;
	${row(10, "flex-end")};
`;

const StyledMenu = styled(ArticleThumbnailMenu)`
	background-color: ${({ theme }) => alpha(theme.colors.backgroundPrimary, 0.98)};
`;
